import { useMemo } from "react";
import { motion, useScroll, useTransform, useMotionValue, useMotionTemplate } from "framer-motion";
import { Coffee, ShieldCheck, Video, ArrowRight, ArrowDown } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { KineticWords } from "./KineticWords";
import { SpinBadge } from "./SpinBadge";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.3 } },
};
const item = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export const Hero = ({ ready = true }) => {
  const { t, lang } = useLanguage();
  const { scrollY } = useScroll();
  const yWord = useTransform(scrollY, [0, 800], [0, 170]);
  const yShield = useTransform(scrollY, [0, 800], [0, -90]);

  const mx = useMotionValue(-600);
  const my = useMotionValue(-600);
  const cursorGlow = useMotionTemplate`radial-gradient(460px circle at ${mx}px ${my}px, rgba(0,245,212,0.10), transparent 70%)`;

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(e.clientX - r.left);
    my.set(e.clientY - r.top);
  };

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  const particles = useMemo(
    () =>
      Array.from({ length: isMobile ? 10 : 12 }, (_, i) => ({
        left: (i * 37 + 13) % 100,
        top: (i * 53 + 29) % 100,
        size: 2 + ((i * 7) % 3),
        delay: (i * 0.37) % 3,
        dur: 2.4 + ((i * 11) % 30) / 10,
      })),
    []
  );

  const trusts = [
    { icon: Coffee, text: t.hero.trust1[lang], testid: "hero-trust-free" },
    { icon: ShieldCheck, text: t.hero.trust2[lang], testid: "hero-trust-nofluff" },
    { icon: Video, text: t.hero.trust3[lang], testid: "hero-trust-online" },
  ];

  return (
    <section
      id="home"
      data-testid="hero-section"
      onMouseMove={onMouseMove}
      className="relative flex min-h-screen flex-col overflow-hidden pt-20"
    >
      {/* background photo layer */}
      <div className="bg-photo pointer-events-none absolute inset-0" aria-hidden="true">
        <img
          src="https://images.unsplash.com/photo-1593427995298-cad6731716d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwzfHxkYXJrJTIwZmluYW5jaWFsJTIwYXJjaGl0ZWN0dXJlJTIwc2t5c2NyYXBlciUyMG5pZ2h0fGVufDB8fHx8MTc5MTE1MzU2MHww&ixlib=rb-4.1.0&q=60&w=1600&auto=format"
          alt=""
          className="h-full w-full object-cover opacity-20 mix-blend-luminosity"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#030712]/70 via-[#030712]/85 to-[#030712]" />
      </div>

      {/* ambient atmospheric light */}
      <div
        className="pointer-events-none absolute -top-40 left-1/4 h-[560px] w-[720px] rounded-full blur-[140px]"
        style={{ background: "radial-gradient(circle, rgba(0,245,212,0.12) 0%, transparent 70%)" }}
        aria-hidden="true"
      />
      <motion.div
        aria-hidden="true"
        className="hero-orb pointer-events-none absolute -left-40 top-10 h-[480px] w-[480px] rounded-full bg-teal-500/15 blur-[130px]"
        animate={isMobile ? {} : { x: [0, 70, 0], y: [0, 40, 0], scale: [1, 1.15, 1] }}
        transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden="true"
        className="hero-orb pointer-events-none absolute -right-32 bottom-0 h-[420px] w-[420px] rounded-full bg-[#1E3A5F]/50 blur-[120px]"
        animate={isMobile ? {} : { x: [0, -60, 0], y: [0, -50, 0], scale: [1, 1.2, 1] }}
        transition={{ duration: 13, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
      />
      <motion.div className="pointer-events-none absolute inset-0" style={{ background: cursorGlow }} aria-hidden="true" />

      <span
        className="pointer-events-none absolute left-5 top-1/2 hidden -translate-y-1/2 font-mono text-[10px] uppercase tracking-[0.35em] text-slate-600 [writing-mode:vertical-lr] lg:block"
        aria-hidden="true"
      >
        01 // ZIRSEN PARTNERSHIP
      </span>

      {/* corner frame marks */}
      <span className="pointer-events-none absolute left-4 top-20 h-5 w-5 border-l-2 border-t-2 border-teal-400/40" aria-hidden="true" />
      <span className="pointer-events-none absolute right-4 top-20 h-5 w-5 border-r-2 border-t-2 border-teal-400/40" aria-hidden="true" />
      <span className="pointer-events-none absolute bottom-4 left-4 h-5 w-5 border-b-2 border-l-2 border-teal-400/40" aria-hidden="true" />
      <span className="pointer-events-none absolute bottom-4 right-4 h-5 w-5 border-b-2 border-r-2 border-teal-400/40" aria-hidden="true" />

      {particles.map((p, i) => (
        <span
          key={i}
          aria-hidden="true"
          className="hero-particle"
          style={{
            left: `${p.left}%`,
            top: `${p.top}%`,
            width: p.size,
            height: p.size,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.dur}s`,
          }}
        />
      ))}

      {/* floating shield + spinning badge (parallax) */}
      <motion.div
        data-testid="hero-visual"
        aria-hidden="true"
        style={{ y: yShield }}
        className="pointer-events-none absolute right-[4%] top-20 z-10 hidden lg:block"
      >
        <motion.img
          src="/logo-icon-light.png"
          alt=""
          className="w-28 xl:w-36"
          style={{ filter: "drop-shadow(0 0 42px rgba(0,245,212,0.45))" }}
          animate={{ y: [0, -18, 0], rotate: [-2.5, 2.5, -2.5] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
        />
        <SpinBadge
          text={lang === "cs" ? "FINANČNÍ ANALÝZA • ZDARMA • 60 SEKUND • " : "FINANCIAL ANALYSIS • FREE • 60 SECONDS • "}
          className="absolute -left-16 -top-16 h-28 w-28 xl:h-32 xl:w-32"
        />
      </motion.div>

      {/* giant outlined brand word drifting on scroll — SVG textLength always fits full width */}
      <motion.div aria-hidden="true" style={{ y: yWord }} className="pointer-events-none absolute -bottom-6 left-0 right-0 select-none">
        <svg viewBox="0 0 600 120" className="w-full" role="presentation">
          <text
            x="300"
            y="102"
            textAnchor="middle"
            textLength="590"
            lengthAdjust="spacingAndGlyphs"
            className="font-display"
            style={{ fontSize: 120, fontWeight: 900, fill: "none", stroke: "rgba(45,212,191,0.10)", strokeWidth: 2 }}
          >
            ZIRSEN
          </text>
        </svg>
      </motion.div>

      <div className="relative mx-auto flex w-full max-w-[1500px] flex-1 flex-col justify-center px-4 py-16 sm:px-8 lg:px-12">
        <motion.div variants={container} initial="hidden" animate={ready ? "show" : "hidden"}>
          <motion.div variants={item} className="flex flex-wrap items-center gap-4">
            <span
              data-testid="hero-badge"
              className="inline-block rounded-full border border-teal-400/30 bg-teal-400/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-teal-300"
            >
              {t.hero.badge[lang]}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-slate-500">
              Zirsen Partnership · Česká Lípa · Online
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="font-display mt-8 font-extrabold uppercase leading-[0.98] tracking-tight"
          >
            <span className="block text-[11vw] text-white sm:text-[9.5vw] lg:text-[7.6vw]">
              <KineticWords key={`l1-${lang}`} text={t.hero.headlineLine1[lang]} play={ready} delay={0.25} />
            </span>
            <span className="mt-1 block text-[11vw] text-teal-300 [text-shadow:0_0_44px_rgba(0,245,212,0.30)] sm:text-[9.5vw] lg:text-[7.6vw]">
              <KineticWords key={`l2-${lang}`} text={t.hero.headlineLine2[lang]} play={ready} delay={0.55} />
            </span>
          </h1>

          <div className="mt-12 flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
            <motion.p
              variants={item}
              data-testid="hero-subtext"
              className="max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
            >
              {t.hero.subtext[lang]}
            </motion.p>
            <motion.div variants={item} className="flex flex-wrap items-center gap-4">
              <motion.a
                href="/matrix"
                data-testid="hero-cta-button"
                whileHover={{ scale: 1.07, rotate: -1.5 }}
                whileTap={{ scale: 0.94 }}
                transition={{ type: "spring", stiffness: 400, damping: 15 }}
                className="group inline-flex items-center gap-2 rounded-full bg-teal-500 px-9 py-4 text-base font-black uppercase tracking-widest text-[#052E2B] shadow-[0_0_30px_rgba(0,245,212,0.25)] transition-colors duration-300 hover:bg-[#00F5D4] hover:shadow-[0_0_50px_rgba(0,245,212,0.55)]"
              >
                {t.hero.ctaAnalysis[lang]}
                <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1.5" />
              </motion.a>
              <motion.a
                href="#booking"
                data-testid="hero-secondary-button"
                whileHover={{ scale: 1.05, rotate: 1 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 15 }}
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.querySelector("#booking");
                  if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -72, duration: 1.4, easing: (x) => 1 - Math.pow(1 - x, 4) });
                  else el?.scrollIntoView({ behavior: "smooth" });
                }}
                className="rounded-full border-2 border-slate-600 px-8 py-4 text-sm font-bold text-slate-200 transition-colors duration-200 hover:border-teal-400 hover:text-teal-300"
              >
                {t.hero.ctaPrimary[lang]}
              </motion.a>
            </motion.div>
          </div>

          <motion.div variants={item} className="mt-12 flex flex-wrap gap-x-8 gap-y-3 border-t border-white/5 pt-8">
            {trusts.map(({ icon: Icon, text, testid }) => (
              <div key={testid} data-testid={testid} className="flex items-center gap-2 text-sm text-slate-300">
                <Icon size={17} className="text-teal-400" />
                <span>{text}</span>
              </div>
            ))}
          </motion.div>

          {/* glass info strip */}
          <motion.div
            variants={item}
            data-testid="hero-card"
            className="glass-dark mt-10 flex max-w-3xl flex-col gap-3 rounded-2xl p-5 sm:flex-row sm:items-center sm:gap-6"
          >
            <div className="flex shrink-0 items-center gap-2">
              <Coffee size={16} className="text-teal-300" />
              <span className="text-xs font-bold uppercase tracking-wider text-teal-300">{t.hero.cardChip[lang]}</span>
            </div>
            <div className="hidden h-8 w-px bg-teal-400/20 sm:block" aria-hidden="true" />
            <div>
              <p className="font-heading text-base font-bold text-white">{t.hero.cardTitle[lang]}</p>
              <p className="mt-0.5 text-sm leading-relaxed text-slate-400">{t.hero.cardText[lang]}</p>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* scroll cue */}
      <div className="pointer-events-none absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 md:flex" aria-hidden="true">
        <span className="font-mono text-[9px] uppercase tracking-[0.35em] text-slate-500">
          {lang === "cs" ? "Scrollujte" : "Scroll"}
        </span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}>
          <ArrowDown size={15} className="text-teal-300" />
        </motion.span>
      </div>
    </section>
  );
};
